/**
 * Global shortcut catalog and key-event matching helpers for the workbench.
 *
 * The catalog is a single source of truth: the `useGlobalShortcuts` hook reads
 * actions from it, the in-app `?` reference dialog renders its labels, and
 * tests assert on the same shape. Keep this file declarative — runtime
 * behavior lives in the hook.
 */

export type ShortcutId =
  | 'commandPalette'
  | 'focusChat'
  | 'toggleLeftNav'
  | 'toggleChat'
  | 'closeFocusedPanel'
  | 'maximizeFocusedPanel'
  | 'openShortcutHelp';

export type ShortcutDefinition = {
  id: ShortcutId;
  /** i18n key under `shortcuts:items.{id}.label` (description for the help dialog). */
  labelKey: string;
  /** A canonical key combo like `mod+k`, `mod+shift+f`, `?`. */
  combo: string;
  /** Pretty display tokens shown in the help dialog and tooltips. */
  display: { mac: string[]; other: string[] };
  /**
   * Whether this shortcut should fire when the user is typing in an input,
   * textarea, or contenteditable element. Defaults to `false`.
   */
  allowInInputs?: boolean;
};

/**
 * The full shortcut catalog. Order is the display order in the help dialog.
 */
export const SHORTCUT_DEFINITIONS: readonly ShortcutDefinition[] = [
  {
    id: 'commandPalette',
    labelKey: 'commandPalette',
    combo: 'mod+k',
    display: { mac: ['⌘', 'K'], other: ['Ctrl', 'K'] },
  },
  {
    id: 'focusChat',
    labelKey: 'focusChat',
    combo: 'mod+/',
    display: { mac: ['⌘', '/'], other: ['Ctrl', '/'] },
  },
  {
    id: 'toggleLeftNav',
    labelKey: 'toggleLeftNav',
    combo: 'mod+b',
    display: { mac: ['⌘', 'B'], other: ['Ctrl', 'B'] },
  },
  {
    id: 'toggleChat',
    labelKey: 'toggleChat',
    combo: 'mod+\\',
    display: { mac: ['⌘', '\\'], other: ['Ctrl', '\\'] },
  },
  {
    id: 'closeFocusedPanel',
    labelKey: 'closeFocusedPanel',
    combo: 'mod+.',
    display: { mac: ['⌘', '.'], other: ['Ctrl', '.'] },
  },
  {
    id: 'maximizeFocusedPanel',
    labelKey: 'maximizeFocusedPanel',
    combo: 'mod+shift+f',
    display: { mac: ['⌘', '⇧', 'F'], other: ['Ctrl', 'Shift', 'F'] },
  },
  {
    id: 'openShortcutHelp',
    labelKey: 'openShortcutHelp',
    combo: '?',
    display: { mac: ['?'], other: ['?'] },
    // `?` opens help — it's a documentation key, not an action to start typing.
    allowInInputs: false,
  },
] as const;

export type KeyEventLike = {
  key: string;
  metaKey: boolean;
  ctrlKey: boolean;
  shiftKey: boolean;
  altKey: boolean;
};

/**
 * Matches a key event against a canonical combo like `mod+shift+k` or `?`.
 *
 * Conventions:
 *  - `mod` matches `metaKey || ctrlKey` (Mac/Windows compatibility).
 *  - Combos are case-insensitive on the base key.
 *  - Modifier order in the combo string is irrelevant.
 *  - For shifted printable keys we compare the produced `event.key` value
 *    (e.g. `?` already has shiftKey true and key === '?'), so the parser
 *    treats the shift modifier as implicit when not present in the combo.
 */
export function matchesCombo(event: KeyEventLike, combo: string): boolean {
  const parts = combo.toLowerCase().split('+').map((p) => p.trim()).filter(Boolean);
  if (parts.length === 0) return false;

  const base = parts[parts.length - 1]!;
  const mods = new Set(parts.slice(0, -1));
  const wantMod = mods.has('mod') || mods.has('cmd') || mods.has('ctrl');
  const wantShift = mods.has('shift');
  const wantAlt = mods.has('alt') || mods.has('option');

  const hasMod = event.metaKey || event.ctrlKey;
  if (wantMod !== hasMod) return false;
  if (wantShift !== event.shiftKey && !isShiftedPrintable(base)) return false;
  if (wantAlt !== event.altKey) return false;

  return event.key.toLowerCase() === base;
}

/**
 * Some keys (like `?`, `:`, `~`) are produced *because* shift is held, but the
 * combo string spells them as the printed character. We tolerate shift in those
 * cases without requiring `shift+` in the combo.
 */
function isShiftedPrintable(base: string): boolean {
  return /^[!@#$%^&*()_+{}|:"<>?~]$/.test(base);
}

/** Returns the shortcut definition matching a given event, or `undefined`. */
export function findShortcut(event: KeyEventLike): ShortcutDefinition | undefined {
  return SHORTCUT_DEFINITIONS.find((def) => matchesCombo(event, def.combo));
}

/**
 * True when the event originated from an editable element — used to gate
 * shortcuts that should not steal typing focus.
 */
export function isEditableTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;
  const tag = target.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
  return false;
}

/** Returns true on Mac so callers can pick the right glyph set for tooltips. */
export function isMacPlatform(): boolean {
  if (typeof navigator === 'undefined') return false;
  return /Mac|iPhone|iPad|iPod/.test(navigator.platform);
}

/** Renders a combo like `mod+shift+f` into `["⌘","⇧","F"]` or `["Ctrl","Shift","F"]`. */
export function shortcutDisplay(def: ShortcutDefinition, mac: boolean): string[] {
  return mac ? [...def.display.mac] : [...def.display.other];
}
